"use client";

import { m, useReducedMotion } from "motion/react";
import Image from "next/image";

import { mergeClasses } from "@trex-2b/libs";

type HeroDividerStripProps = {
  className?: string;
};

const dividerLineClass = "h-px w-[162.75px] shrink-0 bg-[transparent]";
const capImageClass =
  "absolute top-[-3.11%] left-0 h-[106.22%] max-w-none w-full";

/**
 * Figma `8788:6415` — 1152×14; caps `8788:6418` / `8788:6419` (−14 / 1065),
 * lines at 383.75 & 605.5 (w 162.75); spacers 224.75 / 59 / 296.75 — same geometry as before, expressed as `flex-row` + `items-center`.
 */
function HeroDividerStrip({ className }: HeroDividerStripProps) {
  return (
    <div
      className={mergeClasses(
        "pointer-events-none flex h-[14px] max-w-none shrink-0 flex-row items-center overflow-visible",
        className,
      )}
      aria-hidden
    >
      <div className="relative ml-[-14px] h-[14px] w-[173px] shrink-0">
        <div className="absolute inset-0 overflow-hidden">
          <Image
            alt=""
            src="/images/landing/hero-normal.png"
            width={173}
            height={14}
            className={capImageClass}
          />
        </div>
      </div>

      <div className="w-[224.75px] shrink-0" aria-hidden />

      <div className={dividerLineClass} />

      <div className="w-[59px] shrink-0" aria-hidden />

      <div className={dividerLineClass} />

      <div className="w-[296.75px] shrink-0" aria-hidden />

      <div className="relative h-[14px] w-[173px] shrink-0">
        <div className="absolute inset-0 overflow-hidden">
          <Image
            alt=""
            src="/images/landing/hero-normal-reverse.png"
            width={173}
            height={14}
            className={capImageClass}
          />
        </div>
      </div>
    </div>
  );
}

const ORNAMENT_DOTS: [number, number][] = [
  [0, 0],
  [0, 4.91],
  [4.91, 4.91],
  [4.91, 9.82],
  [9.82, 9.82],
  [9.82, 14.73],
  [14.73, 9.82],
  [14.73, 4.91],
  [19.64, 0],
  [19.64, 4.91],
];

const DOT = 3.273;

/** Figma `8788:6420` — intrinsic 22.91×17.9995 */
function ResumePitchOrnament() {
  return (
    <svg
      width={22.91}
      height={18}
      viewBox={`0 0 ${19.64 + DOT} ${14.73 + DOT}`}
      className="shrink-0 text-[#212631]"
      aria-hidden
    >
      {ORNAMENT_DOTS.map(([x, y], i) => (
        <rect
          key={i}
          x={x}
          y={y}
          width={DOT}
          height={DOT}
          rx={1.6365}
          className="fill-current"
        />
      ))}
    </svg>
  );
}

/**
 * Figma `8788:6412` — 1512×292; copy `8788:6431` y=75 h=132; container `8788:6415` y=133 (strip centered with headline, not fixed x=152 on large viewports);
 * ornament `8788:6420` y=239. Line sits between serif lines (z-index under copy).
 */
export function LandingResumePitch() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section
      id="landing-section-why"
      aria-labelledby="landing-resume-pitch-heading"
      className="w-full overflow-x-clip bg-linear-to-b from-white from-0% to-[#eee] to-[143.73%]"
    >
      <div className="relative mx-auto h-[292px] w-full px-[10px] pb-[10px]">
        <div className="relative h-full w-full">
          {/* 全宽 flex 居中，与底部分隔线、箭头 ornament 同一套横向参考，避免单独 left-1/2 产生子像素差 */}
          <m.div
            className="absolute top-[75px] right-0 left-0 z-1 flex justify-center"
            initial={
              shouldReduceMotion
                ? undefined
                : { opacity: 0, y: 26, filter: "blur(8px)" }
            }
            whileInView={
              shouldReduceMotion
                ? undefined
                : { opacity: 1, y: 0, filter: "blur(0px)" }
            }
            viewport={{ amount: 0.65, once: true }}
            transition={{ duration: 0.62, ease: [0.16, 1, 0.3, 1] }}
          >
            <h2
              id="landing-resume-pitch-heading"
              className="w-[851px] max-w-[calc(100%-40px)] font-ancizar text-center font-medium text-[#080808] capitalize not-italic leading-0 tracking-[-3.6px]"
            >
              <span className="inline-block w-full text-[48px] leading-[66px]">
                You wouldn&apos;t hire without a resume.
                <br aria-hidden="true" />
                Why acquire users without one?
              </span>
            </h2>
          </m.div>

          <div className="pointer-events-none absolute inset-0 z-0">
            <div className="absolute top-[133px] right-0 left-0 flex justify-center overflow-x-clip">
              <HeroDividerStrip className="mx-auto shrink-0" />
            </div>

            <m.div
              className="absolute top-[239px] right-0 left-0 flex justify-center"
              initial={
                shouldReduceMotion ? undefined : { opacity: 0, scale: 0.8 }
              }
              whileInView={
                shouldReduceMotion ? undefined : { opacity: 1, scale: 1 }
              }
              viewport={{ amount: 0.65, once: true }}
              transition={{
                duration: 0.5,
                delay: 0.16,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <ResumePitchOrnament />
            </m.div>
          </div>
        </div>
      </div>
    </section>
  );
}
